import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SessionActions } from './session.actions';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import JSEncrypt from 'jsencrypt';
import { environment } from '@environment/environment';
import { GET_QUERY_PARAMS, IS_EMPTY, RestService } from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { ErrorActions } from '../error';
import { ACCOUNT_TYPE, PLAN_NAMES } from '@domain/constants/app-variables';
import { ActivatedRoute, Router } from '@angular/router';
import { LANDING_PAGES, CARRIER_LANDING_PAGES } from '@domain/constants/landing-pages';
import { sessionStateKey } from '.';
import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { IntegrationService } from '@service/integration/integration.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { TokenStoreService } from '../../../core/services/auth/token-store.service';
import { HecateAuthService } from '../../../core/services/auth/hecate-auth.service';

@Injectable({
	providedIn: 'root',
})
export class SessionEffects {
	constructor(
		private actions$: Actions<any>,
		private restService: RestService,
		private urlService: UrlService,
		private router: Router,
		private carrierVettingService: CarrierVettingService,
		private utilService: UtilService,
		private activeRoute: ActivatedRoute,
		private readonly integrationService: IntegrationService,
		private readonly cargochief: Cargochief,
		private readonly hecateAuth: HecateAuthService,
		private readonly tokenStore: TokenStoreService
	) {}

	loginSubmitStarted$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionActions.login.submit.start),
			switchMap(({ email, password, rememberMe }) => {
				// CN-8597 Phase 3 — under the hecate flag, run the password grant
				// (HecateAuthService stores the tokens) then fall through to
				// login.setup.start, which hydrates user state. rememberMe is
				// ignored — refresh-token rotation replaces remember-me.
				if (environment.USE_HECATE_LOGIN) {
					return this.hecateAuth.passwordGrant(email, password).pipe(
						map(() => SessionActions.login.submit.success({ payload: { user: null } })),
						catchError((error) => {
							// hecate returns { error, error_description } on invalid creds.
							const message =
								error?.error?.error_description ?? error?.error?.error ?? 'Invalid email or password.';
							return of(SessionActions.login.submit.failed({ payload: { message } }));
						})
					);
				}

				const encrypt = new JSEncrypt();
				encrypt.setPublicKey(environment.RSA_PUBLIC_KEY);
				const encryptedPassword = encrypt.encrypt(password);
				if (!encryptedPassword) {
					return of(
						SessionActions.login.submit.failed({
							payload: { message: 'Unable to secure credentials. Please try again.' },
						})
					);
				}

				return this.restService
					.POST(this.urlService.loginUrl(), {
						email,
						password: encryptedPassword,
						rememberMe,
						type: environment.app,
					})
					.pipe(
						map((result) =>
							SessionActions.login.submit.success({ payload: { user: result.data } })
						),
						catchError((error) =>
							of(SessionActions.login.submit.failed({ payload: { message: error.error } }))
						)
					);
			})
		)
	);

	loginSubmitSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionActions.login.submit.success),
			switchMap(({ payload }) =>
				of(SessionActions.login.setup.start({ payload: { ...payload, redirect: true } }))
			)
		)
	);

	loginSetupStarted$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionActions.login.setup.start),
			switchMap(({ payload }) =>
				this.restService.GET(this.urlService.loginSetupUrl()).pipe(
					switchMap((loginSetup) => {
						const accountType = loginSetup.account?.accountType?.toUpperCase();
						const isAdmin = accountType === ACCOUNT_TYPE.ADMIN;

						// Only call notificationSettings for non-admin accounts
						if (isAdmin) {
							return of(
								SessionActions.login.setup.success({
									payload: {
										user: loginSetup.user,
										account: loginSetup.account,
										accountSettings: loginSetup.accountSettings,
										userSettings: loginSetup.userSettings,
										products: loginSetup.planProducts,
										notificationSettings: null,
										redirect: payload.redirect,
										addOnSubscriptions: loginSetup.addOnSubscriptions,
										activeIntegrations: loginSetup.activeIntegrations,
										hasActiveTmsIntegration: loginSetup.hasActiveTmsIntegration,
									},
								})
							);
						}

						return this.restService.GET(this.urlService.notificationSettings()).pipe(
							map((notificationSettings) =>
								SessionActions.login.setup.success({
									payload: {
										user: loginSetup.user,
										account: loginSetup.account,
										accountSettings: loginSetup.accountSettings,
										userSettings: loginSetup.userSettings,
										products: loginSetup.planProducts,
										notificationSettings: notificationSettings.data,
										redirect: payload.redirect,
										addOnSubscriptions: loginSetup.addOnSubscriptions,
										activeIntegrations: loginSetup.activeIntegrations,
										hasActiveTmsIntegration: loginSetup.hasActiveTmsIntegration,
									},
								})
							)
						);
					}),
					catchError((error) => {
						if (error.status === 401) {
							localStorage.removeItem(sessionStateKey);
							return;
						}
						return of(SessionActions.login.setup.failed({ payload: { message: error.error } }));
					})
				)
			)
		)
	);

	loginSetupSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SessionActions.login.setup.success),
				filter(({ payload }) => payload.redirect),
				tap(async ({ payload }) => {
					const accountType = payload.account.accountType.toUpperCase();
					const isBrokerOrShipper =
						accountType === ACCOUNT_TYPE.BROKER || accountType === ACCOUNT_TYPE.SHIPPER;

					if (isBrokerOrShipper) {
						await this.integrationService.initialiseIntegrationsType();
					}

					// --- read & normalize redirect path from ?path=... ---
					// Parse from router.url directly: ActivatedRoute injected into a service-level
					// effect points at the root route and doesn't reliably expose query params
					// for hash-routed URLs, which made qpm.get('path') return empty and the
					// effect fall through to the landingPage (dashboard) below.
					// queryParamMap.get() already decodes one level; do NOT decodeURIComponent
					// again or we'd strip the inner URL-encoding (e.g. %5B → "[") and break parseUrl.
					let redirectPath = '';
					try {
						const urlTree = this.router.parseUrl(this.router.url);
						const raw = urlTree.queryParamMap.get('path') || '';
						if (raw) {
							redirectPath = raw.startsWith('/') ? raw : '/' + raw;
						}
					} catch (error) {}

					if (accountType === ACCOUNT_TYPE.BROKER || accountType === ACCOUNT_TYPE.SHIPPER) {
						let landingPage = payload.user.landingPage || LANDING_PAGES.DASHBOARD.value;
						if (landingPage == LANDING_PAGES.PROJECTS.value) {
							landingPage = '/v2/projects'; // ensure leading slash
						}
						// --- use navigateByUrl for pre-serialized URL with querystring ---
						if (redirectPath) {
							await this.router.navigateByUrl(this.router.parseUrl(redirectPath));
						} else {
							await this.router.navigate([landingPage], {
								queryParams: { path: null },
								queryParamsHandling: 'merge',
							});
						}
						return;
					}

					if (
						accountType === ACCOUNT_TYPE.CARRIER &&
						[
							PLAN_NAMES.PRICING.toUpperCase(),
							PLAN_NAMES.CARRIER.toUpperCase(),
							PLAN_NAMES.CARRIER_PLUS_PRICING.toUpperCase(),
							PLAN_NAMES.CARRIER_BASIC.toUpperCase(),
							PLAN_NAMES.CARRIER_PRO.toUpperCase(),
						].includes(payload.account.plan.toUpperCase())
					) {
						let landingPage =
							payload.user.landingPage || CARRIER_LANDING_PAGES.AVAILABLE_LOADS.value;
						// same redirect handling for carriers
						if (redirectPath) {
							await this.router.navigateByUrl(this.router.parseUrl(redirectPath));
						} else {
							await this.router.navigate([landingPage], {
								queryParams: { path: null },
								queryParamsHandling: 'merge',
							});
						}
						return;
					}

					await this.router.navigate(['admin/accounts']);
				})
			),
		{ dispatch: false }
	);

	loginSessionFailure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionActions.login.submit.failed, SessionActions.login.setup.failed),
			tap(() => localStorage.removeItem(sessionStateKey)),
			switchMap(({ payload }) => of(ErrorActions.error.show({ payload })))
		)
	);

	/**
	 * CN-8597 Phase 3 — clear hecate JWTs on explicit user logout. The
	 * interceptor's 401-path already nukes all of localStorage on session
	 * expiry, but a deliberate logout (button click) needs to drop the
	 * tokens too so a subsequent login doesn't pick up a stale refresh
	 * token. No-op when the legacy flow is active (no tokens stored).
	 */
	logoutClearsTokens$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SessionActions.logout.start),
				tap(() => this.tokenStore.clear())
			),
		{ dispatch: false }
	);
}
